/**
 * AHPRA register verification.
 *
 * Phase 1: returns a deterministic mock based on the registration number, with
 * realistic structure so all UI states (verified, conditions, suspended,
 * unknown) can be exercised.
 *
 * Phase 2: wire `supabase.functions.invoke('verify-ahpra-registration', { body })`
 * — the edge function in `supabase/functions/verify-ahpra-registration/`
 * picks one of three documented strategies (responsible scrape, paid third
 * party, or fall back to manual upload).
 */

import { isValidAhpra } from '@/lib/australia';
import type { AhpraVerificationResult } from '@/types/ahpra';

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Hash a string to a positive integer (deterministic mock distribution). */
function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

/**
 * Mock fixture set. Keeping these stable so the demo always behaves the
 * same. In Phase 2 these are replaced with the real edge function response.
 */
const FIXTURES: Record<string, Partial<AhpraVerificationResult['registration']>> = {
  'MED0001234567': {
    registrationNumber: 'MED0001234567',
    fullName: 'Sarah Chen',
    firstName: 'Sarah',
    surname: 'Chen',
    registrationType: 'general',
    status: 'registered',
    specialties: ['Emergency Medicine'],
    registeredFrom: '2018-12-01',
    expiresOn: '2026-09-30',
    conditions: [],
    hasConditions: false,
  },
  'MED0009876543': {
    registrationNumber: 'MED0009876543',
    fullName: 'James Okafor',
    firstName: 'James',
    surname: 'Okafor',
    registrationType: 'specialist',
    status: 'registered',
    specialties: ['General Practice'],
    registeredFrom: '2016-02-01',
    expiresOn: '2026-09-30',
    conditions: [],
    hasConditions: false,
  },
  // Demonstrates a registration WITH conditions — UI should warn.
  'MED0000000001': {
    registrationNumber: 'MED0000000001',
    fullName: 'Demo Conditions',
    firstName: 'Demo',
    surname: 'Conditions',
    registrationType: 'general',
    status: 'registered',
    specialties: ['General Practice'],
    registeredFrom: '2020-01-15',
    expiresOn: '2026-09-30',
    hasConditions: true,
    conditions: [
      { text: 'Must work under supervision of an approved senior practitioner.', imposedAt: '2024-03-01' },
      { text: 'Must complete an approved communication-skills course within 12 months.', imposedAt: '2024-03-01' },
    ],
  },
  // Demonstrates a suspended registration — UI should block placement.
  'MED0000000002': {
    registrationNumber: 'MED0000000002',
    fullName: 'Demo Suspended',
    firstName: 'Demo',
    surname: 'Suspended',
    registrationType: 'general',
    status: 'suspended',
    specialties: [],
    conditions: [],
    hasConditions: false,
  },
};

export const ahpraService = {
  async verify(registrationNumber: string): Promise<AhpraVerificationResult> {
    await delay(900); // simulate live-register round-trip

    const reg = registrationNumber.trim().toUpperCase();
    if (!isValidAhpra(reg)) {
      return {
        ok: false,
        error: 'Invalid AHPRA registration format. Expected MED followed by 10 digits.',
      };
    }

    const fixture = FIXTURES[reg];
    if (fixture) {
      return {
        ok: true,
        registration: {
          ...(fixture as Required<typeof fixture>),
          verifiedAt: new Date().toISOString(),
          source: 'mock',
          profession: 'Medical Practitioner',
        },
      };
    }

    // Deterministic synthesised fixture for unknown numbers — gives the demo
    // a reasonable response no matter what the user types.
    const h = hash(reg);
    const isRegistered = h % 7 !== 0;
    return {
      ok: true,
      registration: {
        registrationNumber: reg,
        fullName: 'Test Practitioner',
        firstName: 'Test',
        surname: 'Practitioner',
        profession: 'Medical Practitioner',
        registrationType: h % 3 === 0 ? 'specialist' : 'general',
        status: isRegistered ? 'registered' : 'lapsed',
        specialties: [],
        registeredFrom: '2019-06-01',
        expiresOn: isRegistered ? '2026-09-30' : '2024-09-30',
        conditions: [],
        hasConditions: false,
        verifiedAt: new Date().toISOString(),
        source: 'mock',
      },
    };
  },
};
